// Requests waiting for a second system administrator (fund changes, setting changes, lifting a freeze): who asked,
// what for, and Approve / Reject. Whoever asked can withdraw their own request but never approve it.
import { shortDate } from '@qor/web-shared';
import { useState } from 'react';
import { fundsApi, type Approval } from '../api';
import { useStaff } from './RequireStaff';

const KIND_LABEL: Record<string, string> = {
  fund_deposit: 'Deposit',
  fund_withdrawal: 'Withdrawal',
  fund_adjustment: 'Adjustment',
  fund_advance: 'Advance',
  fund_advance_repayment: 'Advance repaid',
  fund_write_off: 'Write off',
  fund_write_off_recovery: 'Written-off debt recovered',
  processor_topup: 'Wire to processor',
  processor_adjustment: 'Processor ledger correction',
  setting_change: 'Settings change',
  loads_resume: 'Resume loads',
  points_pool_deposit: 'Points pool deposit',
  points_pool_adjustment: 'Points pool correction',
};
export const approvalKind = (kind: string) => KIND_LABEL[kind] ?? kind;

/** What the request asks, in one line. */
export function approvalSummary(a: Approval): string {
  const p = a.payload;
  if (a.kind === 'setting_change')
    return [
      ...Object.entries(p.set ?? {}).map(
        ([k, v]) => `${k.replace(/^(funding|points|partnerApi)\./, '')} → ${v === null ? 'none' : String(v)}`,
      ),
      ...(p.clear ?? []).map((k) => `${k.replace(/^(funding|points|partnerApi)\./, '')} → inherited`),
    ].join(', ');
  if (a.kind.startsWith('points_pool_'))
    return [`${String((p as { points?: number }).points)} points`, p.reference && `ref ${p.reference}`, p.memo]
      .filter(Boolean)
      .join(' · ');
  if (a.kind === 'loads_resume')
    return a.programId ? 'Lift the freeze on this partner' : 'Lift the freeze on every partner';
  return [
    p.amount && `${p.amount} ${p.currency ?? ''}`,
    p.provider,
    p.dueAt && `due ${p.dueAt.slice(0, 10)}`,
    p.reference && `ref ${p.reference}`,
    p.memo,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What Approve asks first: a deposit that may be the same wire as another says so. */
export function approveQuestion(a: Approval): string {
  const n = a.possibleDuplicates?.length ?? 0;
  return n > 0
    ? `Approve: ${approvalKind(a.kind)}? ${n === 1 ? 'Another deposit' : `${n} other deposits`} of this partner may be the same wire (same amount or reference). Check the bank statement first: approving credits the fund now.`
    : `Approve: ${approvalKind(a.kind)}? It is done now.`;
}

/** A deposit's bills, each saved by the browser on click; a system admin attaches one while the deposit waits. */
function Bills({
  approval,
  mayAttach,
  onAttached,
}: {
  approval: Approval;
  mayAttach: boolean;
  onAttached?: (ok: boolean, message: string) => void;
}) {
  const [sending, setSending] = useState(false);
  const bills = approval.bills ?? [];
  async function attach(file: File | undefined) {
    if (!file || !onAttached) return;
    setSending(true);
    const r = await fundsApi.addBill(approval.id, file);
    setSending(false);
    onAttached(r.ok, r.ok ? 'Bill attached.' : r.error);
  }
  return (
    <div style={{ marginTop: 4 }}>
      {bills.length === 0 ? (
        <small>No bill</small>
      ) : (
        bills.map((b, i) => (
          <button
            key={b.id}
            type="button"
            className="qc-button-ghost"
            style={{ marginRight: 6 }}
            onClick={() =>
              void fundsApi
                .openBill(approval.id, b, i)
                .catch(() => onAttached?.(false, 'The bill could not be opened.'))
            }
          >
            Bill {i + 1} ({b.contentType === 'application/pdf' ? 'PDF' : 'image'})
          </button>
        ))
      )}
      {mayAttach && approval.status === 'pending' && onAttached && bills.length < 5 && (
        <label className="qc-button-ghost" style={{ cursor: 'pointer' }}>
          {sending ? 'Attaching…' : 'Attach bill'}
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            hidden
            disabled={sending}
            onChange={(e) => {
              void attach(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      )}
    </div>
  );
}

export function ApprovalsTable({
  items,
  busy,
  showProgram = false,
  onDecide,
  onChanged,
  empty = 'Nothing is waiting for approval.',
}: {
  items: Approval[];
  busy: boolean;
  showProgram?: boolean;
  /** What to say when there is nothing (a history, not only the queue). */
  empty?: string;
  onDecide: (approval: Approval, approve: boolean) => void;
  /** After a bill is attached (or fails to be): the page refreshes and says so. */
  onChanged?: (ok: boolean, message: string) => void;
}) {
  const { user } = useStaff();
  const mayDecide = user.role === 'SystemAdmin';
  if (items.length === 0) return <p className="qc-empty">{empty}</p>;
  return (
    <table className="qc-plain-table">
      <thead>
        <tr>
          <th>Asked</th>
          {showProgram && <th>Partner</th>}
          <th>What</th>
          <th>Details</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((a) => {
          const mine = a.requestedBy.id === user.id;
          return (
            <tr key={a.id}>
              <td>
                {shortDate(a.createdAt)}
                <br />
                <small>{a.requestedBy.label}</small>
              </td>
              {showProgram && <td>{a.programName ?? (a.programId ? '—' : 'All / QOR')}</td>}
              <td>{approvalKind(a.kind)}</td>
              <td>
                {approvalSummary(a)}
                {(a.possibleDuplicates?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <span className="qc-badge qc-badge-danger">Possible duplicate</span>{' '}
                    <small>
                      {a.possibleDuplicates!.length === 1
                        ? 'Another deposit'
                        : `${a.possibleDuplicates!.length} other deposits`}{' '}
                      of this partner, of the same amount or reference, told around then. Check the bank statement
                      before approving.
                    </small>
                  </div>
                )}
                {a.payload.confirmedNotDuplicate && (
                  <div>
                    <small>The asker checked: another wire than a deposit of the same amount.</small>
                  </div>
                )}
                {a.kind === 'fund_deposit' && <Bills approval={a} mayAttach={mayDecide} onAttached={onChanged} />}
              </td>
              <td>
                <span
                  className={`qc-badge ${a.status === 'approved' ? 'qc-badge-success' : a.status === 'pending' ? 'qc-badge-warning' : a.status === 'failed' ? 'qc-badge-danger' : 'qc-badge-muted'}`}
                >
                  {a.status}
                </span>
                {a.note && (
                  <>
                    <br />
                    <small>{a.note}</small>
                  </>
                )}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {a.status === 'pending' && mayDecide && (
                  <>
                    {!mine && (
                      <button
                        type="button"
                        className="qor-button qor-button-primary"
                        disabled={busy}
                        onClick={() => onDecide(a, true)}
                      >
                        Approve
                      </button>
                    )}{' '}
                    <button
                      type="button"
                      className="qc-button-ghost"
                      disabled={busy}
                      onClick={() => onDecide(a, false)}
                    >
                      {mine ? 'Withdraw' : 'Reject'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
